if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        this.bissObserver = new MutationObserver(() => this.enforceBissState());
        this.bissObserver.observe(this.submitButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
          childList: true,
          subtree: true,
        });
        this.enforceBissState();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.bissObserver?.disconnect();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.isBissPurchasable()) {
          this.enforceBissState();
          return;
        }
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error && this.isBissPurchasable()) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            this.enforceBissState();
          });
      }

      isBissPurchasable() {
        const marker = this.form.querySelector('[data-biss-purchasable]');
        if (!marker) return true;
        if (marker.value !== 'true') return false;
        if (this.dataset.bissPurchaseState !== 'backorder') return true;

        const requiredProperties = [
          'Backorder',
          '_biss_backorder',
          '_biss_backorder_bucket',
          '_biss_backorder_decision',
        ];
        return requiredProperties.every((property) => {
          const input = this.form.querySelector(`[name="properties[${property}]"][data-biss-backorder-property]`);
          return input?.value?.trim();
        });
      }

      enforceBissState() {
        const marker = this.form.querySelector('[data-biss-purchasable]');
        if (!marker) return;

        const purchasable = this.isBissPurchasable();
        const state = this.dataset.bissPurchaseState || 'sold_out';
        const label = purchasable
          ? this.submitButton.dataset.bissLabel || window.variantStrings.addToCart
          : window.variantStrings.soldOut;

        if (!purchasable) {
          if (!this.submitButton.disabled) this.submitButton.setAttribute('disabled', 'disabled');
          if (this.submitButton.getAttribute('aria-disabled') !== 'true') {
            this.submitButton.setAttribute('aria-disabled', 'true');
          }
          if (!this.variantIdInput.disabled) this.variantIdInput.disabled = true;
        }
        if (state === 'backorder' || !purchasable) {
          if (this.submitButtonText.textContent !== label) this.submitButtonText.textContent = label;
        }
      }

      onVariantChange(event) {
        const data = event?.data;
        if (!data?.html || data.sectionId !== this.dataset.sectionId) return;

        const sourceButton = data.html.getElementById(`ProductSubmitButton-${data.sectionId}`);
        const sourceFormElement = sourceButton?.closest('product-form');
        const sourceForm = sourceFormElement?.querySelector('form');
        if (!sourceButton || !sourceFormElement || !sourceForm) return;

        this.dataset.bissPurchaseState = sourceFormElement.dataset.bissPurchaseState || 'sold_out';
        this.submitButton.dataset.bissPurchaseState = sourceButton.dataset.bissPurchaseState || 'sold_out';
        this.submitButton.dataset.bissLabel = sourceButton.dataset.bissLabel || window.variantStrings.soldOut;

        this.form.querySelectorAll('[data-biss-purchasable], [data-biss-backorder-property]').forEach((input) => input.remove());
        sourceForm
          .querySelectorAll('[data-biss-purchasable], [data-biss-backorder-property]')
          .forEach((input) => this.form.append(input.cloneNode(true)));
        const sourceVariantIdInput = sourceForm.querySelector('[name="id"]');
        this.variantIdInput.disabled = !sourceVariantIdInput || sourceVariantIdInput.disabled;

        const currentNotice = this.querySelector('[data-biss-backorder-notice]');
        const sourceNotice = sourceFormElement.querySelector('[data-biss-backorder-notice]');
        if (currentNotice && sourceNotice) {
          currentNotice.innerHTML = sourceNotice.innerHTML;
          currentNotice.toggleAttribute('hidden', sourceNotice.hasAttribute('hidden'));
        }

        const currentAcceleratedCheckout = this.querySelector('[data-biss-accelerated-checkout]');
        const sourceAcceleratedCheckout = sourceFormElement.querySelector('[data-biss-accelerated-checkout]');
        if (currentAcceleratedCheckout) {
          currentAcceleratedCheckout.toggleAttribute(
            'hidden',
            !sourceAcceleratedCheckout || sourceAcceleratedCheckout.hasAttribute('hidden')
          );
        }

        if (sourceButton.hasAttribute('disabled')) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
        }
        this.submitButtonText.textContent = this.submitButton.dataset.bissLabel;
        this.enforceBissState();
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
        this.enforceBissState();
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
